import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import { Calendar, User, Tag, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pageSettings, setPageSettings] = useState({
    page_title: 'Blog',
    page_subtitle: "Latest news, insights, and updates from NYC's Premier Tech Community",
    meta_title: 'Blog | Pulse NYC',
    meta_description: "Latest news, insights, and updates from NYC's Premier Tech Community.",
    posts_per_page: 12
  });

  useEffect(() => {
    fetchPageSettings();
    fetchCategories();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (pageSettings.posts_per_page) {
      fetchPosts();
    }
  }, [page, selectedCategory, pageSettings.posts_per_page]);

  useEffect(() => {
    // Set meta tags from page settings
    document.title = pageSettings.meta_title;
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', pageSettings.meta_description);
  }, [pageSettings]);

  const fetchPageSettings = async () => {
    try {
      const response = await axios.get(`${API}/blog/settings`);
      setPageSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch blog page settings:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: pageSettings.posts_per_page });
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/blog?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      console.error('Failed to fetch blog posts:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories/list`);
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading && posts.length === 0) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar darkHero={true} />
      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{pageSettings.page_title}</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {pageSettings.page_subtitle}
            </p>
          </div>
        </section>

        {/* Categories Filter */}
        {categories.length > 0 && (
          <section className="border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-500 mr-2">Filter:</span>
                <button
                  onClick={() => { setSelectedCategory(null); setPage(1); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    !selectedCategory 
                      ? 'bg-gray-900 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => { setSelectedCategory(category); setPage(1); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Blog Posts Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No blog posts found.</p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <article 
                      key={post.id} 
                      className="bg-white rounded-xl overflow-hidden shadow-sm border hover:shadow-lg transition-shadow group"
                    >
                      <Link to={`/blog/${post.slug}/`}>
                        {post.featured_image ? (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={post.featured_image}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        ) : (
                          <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <span className="text-4xl text-gray-400">{post.title[0]}</span>
                          </div>
                        )}
                      </Link>
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                          {post.category && (
                            <span className="flex items-center gap-1">
                              <Tag size={14} />
                              {post.category}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(post.published_at)}
                          </span>
                        </div>
                        <Link to={`/blog/${post.slug}/`}>
                          <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-600 transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                        </Link>
                        {post.excerpt && (
                          <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                        )}
                        <Link 
                          to={`/blog/${post.slug}/`}
                          className="inline-flex items-center text-gray-900 font-medium hover:gap-2 transition-all"
                        >
                          Read More <ArrowRight size={16} className="ml-1" />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12 gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setPage(i + 1)}
                        className={`px-4 py-2 rounded-lg ${
                          page === i + 1
                            ? 'bg-gray-900 text-white'
                            : 'border text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BlogList;
